import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import SEOHead from '../../components/base/SEOHead';

export default function ReferencePage() {
  const [activeCategory, setActiveCategory] = useState('cli');
  const [searchQuery, setSearchQuery] = useState('');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: '参考文档',
    description: 'OpenClaw CLI 命令、配置项和 API 接口完整参考文档',
    url: `${siteUrl}/reference`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  const references = {
    cli: [
      {
        command: 'openclaw onboard',
        description: '初始化 OpenClaw，配置模型和渠道',
        usage: 'openclaw onboard [options]',
        options: [
          { flag: '--skip-channels', desc: '跳过渠道配置' },
          { flag: '--skip-models', desc: '跳过模型配置' },
          { flag: '--config <path>', desc: '指定配置文件路径' }
        ]
      },
      {
        command: 'openclaw gateway',
        description: '启动 API 网关服务',
        usage: 'openclaw gateway [command] [options]',
        options: [
          { flag: 'start', desc: '启动网关' },
          { flag: 'stop', desc: '停止网关' },
          { flag: 'restart', desc: '重启网关' },
          { flag: '--port <port>', desc: '指定端口' }
        ]
      },
      {
        command: 'openclaw channels',
        description: '管理消息渠道',
        usage: 'openclaw channels [command]',
        options: [
          { flag: 'list', desc: '列出所有渠道' },
          { flag: 'pair', desc: '配对新渠道' },
          { flag: 'remove <id>', desc: '移除渠道' },
          { flag: 'status', desc: '查看渠道状态' }
        ]
      },
      {
        command: 'openclaw models',
        description: '管理 AI 模型配置',
        usage: 'openclaw models [command]',
        options: [
          { flag: 'list', desc: '列出已配置的模型' },
          { flag: 'add', desc: '添加新模型' },
          { flag: 'test <model>', desc: '测试模型连接' },
          { flag: 'set-default <model>', desc: '设置默认模型' }
        ]
      },
      {
        command: 'openclaw sessions',
        description: '管理对话会话',
        usage: 'openclaw sessions [command]',
        options: [
          { flag: 'list', desc: '列出所有会话' },
          { flag: 'show <id>', desc: '查看会话详情' },
          { flag: 'clear <id>', desc: '清除会话历史' },
          { flag: 'export <id>', desc: '导出会话数据' }
        ]
      },
      {
        command: 'openclaw doctor',
        description: '诊断系统问题',
        usage: 'openclaw doctor',
        options: [
          { flag: '--fix', desc: '自动修复问题' },
          { flag: '--verbose', desc: '显示详细信息' }
        ]
      }
    ],
    config: [
      {
        section: 'providers',
        description: '模型提供商配置',
        example: `providers:
  anthropic:
    api_key: "sk-ant-..."
    model: "claude-3-5-sonnet-20241022"
    max_tokens: 4096
    temperature: 0.7
  
  openai:
    api_key: "sk-..."
    model: "gpt-4o"
    temperature: 0.7`,
        fields: [
          { name: 'api_key', type: 'string', required: true, desc: 'API 密钥' },
          { name: 'model', type: 'string', required: true, desc: '模型名称' },
          { name: 'max_tokens', type: 'number', required: false, desc: '最大 token 数' },
          { name: 'temperature', type: 'number', required: false, desc: '温度参数 (0-1)' }
        ]
      },
      {
        section: 'channels',
        description: '消息渠道配置',
        example: `channels:
  telegram:
    enabled: true
    bot_token: "123456:ABC-DEF..."
    
  whatsapp:
    enabled: true
    phone_number: "+1234567890"
    
  discord:
    enabled: true
    bot_token: "..."
    guild_id: "..."`,
        fields: [
          { name: 'enabled', type: 'boolean', required: true, desc: '是否启用' },
          { name: 'bot_token', type: 'string', required: true, desc: 'Bot Token' },
          { name: 'phone_number', type: 'string', required: false, desc: '电话号码（WhatsApp）' }
        ]
      },
      {
        section: 'agent',
        description: '智能体配置',
        example: `agent:
  name: "我的助手"
  system_prompt: "你是一个有帮助的 AI 助手"
  
  memory:
    enabled: true
    max_messages: 100
    
  tools:
    - name: "web_search"
      enabled: true
    - name: "calculator"
      enabled: true`,
        fields: [
          { name: 'name', type: 'string', required: true, desc: '智能体名称' },
          { name: 'system_prompt', type: 'string', required: true, desc: '系统提示词' },
          { name: 'memory.enabled', type: 'boolean', required: false, desc: '启用记忆' },
          { name: 'tools', type: 'array', required: false, desc: '工具列表' }
        ]
      },
      {
        section: 'gateway',
        description: '网关配置',
        example: `gateway:
  port: 3000
  host: "0.0.0.0"
  
  auth:
    enabled: true
    type: "api_key"
    
  rate_limit:
    enabled: true
    requests_per_minute: 60`,
        fields: [
          { name: 'port', type: 'number', required: true, desc: '监听端口' },
          { name: 'host', type: 'string', required: true, desc: '监听地址' },
          { name: 'auth.enabled', type: 'boolean', required: false, desc: '启用认证' },
          { name: 'rate_limit.requests_per_minute', type: 'number', required: false, desc: '每分钟请求数' }
        ]
      }
    ],
    api: [
      {
        endpoint: 'POST /api/chat',
        description: '发送消息到智能体',
        request: {
          message: 'string (required) - 用户消息',
          session_id: 'string (optional) - 会话 ID',
          agent: 'string (optional) - 智能体名称',
          stream: 'boolean (optional) - 是否流式返回'
        },
        response: {
          response: 'string - AI 回复',
          session_id: 'string - 会话 ID',
          timestamp: 'string - 时间戳'
        }
      },
      {
        endpoint: 'GET /api/sessions',
        description: '获取会话列表',
        request: {
          limit: 'number (optional) - 返回数量',
          offset: 'number (optional) - 偏移量'
        },
        response: {
          sessions: 'array - 会话列表',
          total: 'number - 总数'
        }
      },
      {
        endpoint: 'GET /api/sessions/:id',
        description: '获取会话详情',
        request: {},
        response: {
          id: 'string - 会话 ID',
          messages: 'array - 消息列表',
          created_at: 'string - 创建时间'
        }
      },
      {
        endpoint: 'DELETE /api/sessions/:id',
        description: '删除会话',
        request: {},
        response: {
          success: 'boolean - 是否成功'
        }
      }
    ]
  };

  const filteredReferences = references[activeCategory as keyof typeof references].filter((item: any) => {
    const searchLower = searchQuery.toLowerCase();
    if (activeCategory === 'cli') {
      return item.command.toLowerCase().includes(searchLower) || 
             item.description.toLowerCase().includes(searchLower);
    } else if (activeCategory === 'config') {
      return item.section.toLowerCase().includes(searchLower) || 
             item.description.toLowerCase().includes(searchLower);
    } else {
      return item.endpoint.toLowerCase().includes(searchLower) || 
             item.description.toLowerCase().includes(searchLower);
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <SEOHead
        title="参考文档 | OpenClaw CLI、配置、API 完整参考"
        description="OpenClaw CLI 命令、配置项和 API 接口完整参考文档 - 快速查找所需信息"
        keywords="OpenClaw参考文档, CLI命令, API接口, 配置参考, 开发文档"
        canonical={`${siteUrl}/reference`}
        jsonLd={jsonLd}
      />
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-10">
          {/* Hero Section */}
          <AnimatedSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
              <i className="ri-book-2-line text-blue-400"></i>
              <span className="text-sm text-blue-400 font-medium">API 参考文档</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              参考文档
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              完整的 CLI 命令、配置项和 API 接口参考文档
            </p>
          </AnimatedSection>

          {/* Search Bar */}
          <AnimatedSection className="mb-8">
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl"></i>
                <input
                  type="text"
                  placeholder="搜索命令、配置项或 API..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                />
              </div>
            </div>
          </AnimatedSection>

          {/* Category Tabs */}
          <AnimatedSection className="mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { id: 'cli', label: 'CLI 命令', icon: 'ri-terminal-line' },
                { id: 'config', label: '配置参考', icon: 'ri-settings-3-line' },
                { id: 'api', label: 'API 接口', icon: 'ri-code-s-slash-line' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap ${
                    activeCategory === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                      : 'bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-white border border-slate-700/50'
                  }`}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </AnimatedSection>

          {/* CLI Commands */}
          {activeCategory === 'cli' && (
            <AnimatedSection>
              <div className="space-y-6">
                {filteredReferences.map((cmd: any, idx: number) => (
                  <div key={idx} className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 hover:border-blue-500/30 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <code className="text-xl font-mono text-cyan-400">{cmd.command}</code>
                        <p className="text-slate-400 mt-2">{cmd.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <div className="text-sm text-slate-500 mb-2">用法</div>
                        <div className="bg-slate-950/50 rounded-lg p-3">
                          <code className="text-sm text-slate-300">{cmd.usage}</code>
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-slate-500 mb-2">选项</div>
                        <div className="space-y-2">
                          {cmd.options.map((opt: any, optIdx: number) => (
                            <div key={optIdx} className="flex items-start gap-3 text-sm">
                              <code className="text-cyan-400 font-mono whitespace-nowrap">{opt.flag}</code>
                              <span className="text-slate-400">{opt.desc}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          )}

          {/* Config Reference */}
          {activeCategory === 'config' && (
            <AnimatedSection>
              <div className="space-y-6">
                {filteredReferences.map((config: any, idx: number) => (
                  <div key={idx} className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 hover:border-blue-500/30 transition-all">
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-white mb-2">{config.section}</h3>
                      <p className="text-slate-400">{config.description}</p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <div className="text-sm text-slate-500 mb-2">配置示例</div>
                        <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800">
                          <pre className="text-sm text-slate-300 overflow-x-auto">{config.example}</pre>
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-slate-500 mb-3">配置字段</div>
                        <div className="space-y-3">
                          {config.fields.map((field: any, fieldIdx: number) => (
                            <div key={fieldIdx} className="bg-slate-800/30 rounded-lg p-4">
                              <div className="flex items-center gap-3 mb-2">
                                <code className="text-cyan-400 font-mono">{field.name}</code>
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                                  {field.type}
                                </span>
                                {field.required && (
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                                    必填
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-400">{field.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          )}

          {/* API Reference */}
          {activeCategory === 'api' && (
            <AnimatedSection>
              <div className="space-y-6">
                {filteredReferences.map((api: any, idx: number) => (
                  <div key={idx} className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 hover:border-blue-500/30 transition-all">
                    <div className="mb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-lg font-mono text-sm font-bold ${
                          api.endpoint.startsWith('POST') ? 'bg-green-500/20 text-green-400' :
                          api.endpoint.startsWith('GET') ? 'bg-blue-500/20 text-blue-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {api.endpoint.split(' ')[0]}
                        </span>
                        <code className="text-cyan-400 font-mono text-lg">{api.endpoint.split(' ')[1]}</code>
                      </div>
                      <p className="text-slate-400">{api.description}</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="text-sm text-slate-500 mb-3">请求参数</div>
                        <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800">
                          {Object.keys(api.request).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(api.request).map(([key, value]) => (
                                <div key={key} className="text-sm">
                                  <code className="text-cyan-400">{key}</code>
                                  <span className="text-slate-500"> - </span>
                                  <span className="text-slate-300">{value as string}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500 text-sm">无参数</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-slate-500 mb-3">响应字段</div>
                        <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800">
                          <div className="space-y-2">
                            {Object.entries(api.response).map(([key, value]) => (
                              <div key={key} className="text-sm">
                                <code className="text-cyan-400">{key}</code>
                                <span className="text-slate-500"> - </span>
                                <span className="text-slate-300">{value as string}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}